// 8-bit Retro Sound Synthesizer via Web Audio API (zero external assets needed)
class SoundFx {
  constructor() {
    this.ctx = null;
    this.enabled = true;
  }

  init() {
    if (!this.ctx && (window.AudioContext || window.webkitAudioContext)) {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        this.ctx = new AudioCtx();
      } catch (e) {}
    }
    if (this.ctx && this.ctx.state === "suspended") {
      this.ctx.resume();
    }
  }

  playLaser() {
    if (!this.enabled || !this.ctx) return;
    try {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.type = "sawtooth";
      osc.frequency.setValueAtTime(880, this.ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(150, this.ctx.currentTime + 0.12);
      gain.gain.setValueAtTime(0.15, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, this.ctx.currentTime + 0.12);
      osc.connect(gain);
      gain.connect(this.ctx.destination);
      osc.start();
      osc.stop(this.ctx.currentTime + 0.12);
    } catch (e) {}
  }

  playExplosion() {
    if (!this.enabled || !this.ctx) return;
    try {
      const bufferSize = this.ctx.sampleRate * 0.25;
      const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
      const data = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) {
        data[i] = Math.random() * 2 - 1;
      }
      const noise = this.ctx.createBufferSource();
      noise.buffer = buffer;
      const filter = this.ctx.createBiquadFilter();
      filter.type = "lowpass";
      filter.frequency.setValueAtTime(800, this.ctx.currentTime);
      filter.frequency.linearRampToValueAtTime(80, this.ctx.currentTime + 0.25);
      const gain = this.ctx.createGain();
      gain.gain.setValueAtTime(0.25, this.ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.01, this.ctx.currentTime + 0.25);
      noise.connect(filter);
      filter.connect(gain);
      gain.connect(this.ctx.destination);
      noise.start();
    } catch (e) {}
  }

  playVictory() {
    if (!this.enabled || !this.ctx) return;
    try {
      const notes = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6
      notes.forEach((freq, idx) => {
        const osc = this.ctx.createOscillator();
        const gain = this.ctx.createGain();
        osc.type = "triangle";
        osc.frequency.setValueAtTime(freq, this.ctx.currentTime + idx * 0.1);
        gain.gain.setValueAtTime(0.2, this.ctx.currentTime + idx * 0.1);
        gain.gain.linearRampToValueAtTime(0.01, this.ctx.currentTime + idx * 0.1 + 0.2);
        osc.connect(gain);
        gain.connect(this.ctx.destination);
        osc.start(this.ctx.currentTime + idx * 0.1);
        osc.stop(this.ctx.currentTime + idx * 0.1 + 0.22);
      });
    } catch (e) {}
  }
}

export default function initBugSquasher() {
  const gameContainer = document.getElementById("bugGameContainer");
  const presentationContainer = document.getElementById("heroPresentation");
  const canvas = document.getElementById("spaceArenaCanvas");
  const skipBtn = document.getElementById("skipGameBtn");
  const replayBtn = document.getElementById("replayGameBtn");
  const soundToggleBtn = document.getElementById("soundToggleBtn");

  if (!gameContainer || !presentationContainer || !canvas) return;

  const ctx = canvas.getContext("2d");
  const sfx = new SoundFx();

  let animationFrameId;
  let squashedCount = 0;
  let gameActive = true;
  let mouse = { x: -100, y: -100, isHovering: false };
  let lasers = [];
  let particles = [];
  let floatingTexts = [];
  let stars = [];

  // Update canvas sizing
  function resizeCanvas() {
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    const w = rect.width;
    const h = rect.height;

    canvas.width = Math.floor(w * dpr);
    canvas.height = Math.floor(h * dpr);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(dpr, dpr);

    // Keep bug positions proportional if canvas resizes
    if (alienBugs && alienBugs.length === 3) {
      alienBugs[0].baseX = w * 0.22;
      alienBugs[0].baseY = h * 0.38;
      alienBugs[1].baseX = w * 0.5;
      alienBugs[1].baseY = h * 0.32;
      alienBugs[2].baseX = w * 0.78;
      alienBugs[2].baseY = h * 0.42;
    }
  }

  // Generate dynamic stars
  function initStars() {
    stars = [];
    const count = 140;
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || 1200;
    const h = rect.height || 560;
    for (let i = 0; i < count; i++) {
      stars.push({
        x: Math.random() * w,
        y: Math.random() * h,
        size: Math.random() * 2.2 + 0.6,
        speed: Math.random() * 0.85 + 0.25,
        alpha: Math.random() * 0.8 + 0.2,
        twinkle: Math.random() * 0.03 + 0.01,
      });
    }
  }

  // 3 Alien Bug definitions
  let alienBugs = [];
  function initBugs() {
    const rect = canvas.getBoundingClientRect();
    const w = rect.width || 1200;
    const h = rect.height || 560;

    alienBugs = [
      {
        id: "bug1",
        name: "NullPointer-Bug",
        subtext: "NULL_POINTER_EXCEPTION",
        x: w * 0.22,
        y: h * 0.38,
        baseX: w * 0.22,
        baseY: h * 0.38,
        radius: 44,
        color: "#a855f7", // Neon Purple
        accentColor: "#38bdf8", // Neon Cyan
        speed: 1.8,
        phase: 0,
        alive: true,
        type: "beetle",
      },
      {
        id: "bug2",
        name: "RaceCondition-Wasp",
        subtext: "CONCURRENCY_CONFLICT",
        x: w * 0.5,
        y: h * 0.32,
        baseX: w * 0.5,
        baseY: h * 0.32,
        radius: 42,
        color: "#f59e0b", // Electric Amber
        accentColor: "#ef4444", // Red Laser
        speed: 2.5,
        phase: 2.1,
        alive: true,
        type: "wasp",
      },
      {
        id: "bug3",
        name: "MemoryLeak-Crawler",
        subtext: "HEAP_OVERFLOW_THREAT",
        x: w * 0.78,
        y: h * 0.42,
        baseX: w * 0.78,
        baseY: h * 0.42,
        radius: 46,
        color: "#10b981", // Radioactive Emerald
        accentColor: "#34d399", // Neon Mint
        speed: 1.5,
        phase: 4.2,
        alive: true,
        type: "crawler",
      },
    ];
  }

  // Draw Space Alien Bug on Canvas
  function drawBug(bug, time) {
    if (!bug.alive) return;

    ctx.save();
    ctx.translate(bug.x, bug.y);

    // Floating bobbing angle
    const tilt = Math.sin(time * bug.speed) * 0.15;
    ctx.rotate(tilt);

    // Outer Energy Shield / Glow
    const pulseGlow = Math.sin(time * 4) * 6 + 18;
    const gradient = ctx.createRadialGradient(0, 0, 10, 0, 0, bug.radius + pulseGlow);
    gradient.addColorStop(0, bug.color + "99");
    gradient.addColorStop(0.6, bug.color + "33");
    gradient.addColorStop(1, "transparent");
    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(0, 0, bug.radius + pulseGlow, 0, Math.PI * 2);
    ctx.fill();

    // Fluttering Alien Wings / Energy Fins
    const wingFlap = Math.sin(time * 16) * 18;
    ctx.fillStyle = bug.accentColor + "aa";

    // Left Wing
    ctx.beginPath();
    ctx.ellipse(-bug.radius * 0.8, -bug.radius * 0.3 + wingFlap * 0.2, 18, 9, -0.6, 0, Math.PI * 2);
    ctx.fill();

    // Right Wing
    ctx.beginPath();
    ctx.ellipse(bug.radius * 0.8, -bug.radius * 0.3 + wingFlap * 0.2, 18, 9, 0.6, 0, Math.PI * 2);
    ctx.fill();

    // Bug Body
    ctx.fillStyle = bug.color;
    ctx.strokeStyle = bug.accentColor;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.ellipse(0, 0, bug.radius * 0.7, bug.radius * 0.85, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.stroke();

    // Bug Carapace Armor Plates
    ctx.fillStyle = "#0f172a";
    ctx.beginPath();
    ctx.ellipse(0, 6, bug.radius * 0.45, bug.radius * 0.45, 0, 0, Math.PI * 2);
    ctx.fill();

    // Glowing Alien Eyes
    ctx.fillStyle = "#ffffff";
    ctx.shadowColor = bug.accentColor;
    ctx.shadowBlur = 10;
    ctx.beginPath();
    ctx.arc(-11, -12, 5.5, 0, Math.PI * 2);
    ctx.arc(11, -12, 5.5, 0, Math.PI * 2);
    ctx.fill();

    // Eye pupils
    ctx.fillStyle = "#ef4444";
    ctx.shadowBlur = 0;
    ctx.beginPath();
    ctx.arc(-11 + Math.sin(time * 2) * 1.5, -12, 2.5, 0, Math.PI * 2);
    ctx.arc(11 + Math.sin(time * 2) * 1.5, -12, 2.5, 0, Math.PI * 2);
    ctx.fill();

    // Antennas with glowing tips
    ctx.strokeStyle = bug.accentColor;
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(-10, -22);
    ctx.quadraticCurveTo(-18, -36, -24 + Math.sin(time * 3) * 4, -40);
    ctx.moveTo(10, -22);
    ctx.quadraticCurveTo(18, -36, 24 - Math.sin(time * 3) * 4, -40);
    ctx.stroke();

    // Antenna bulbs
    ctx.fillStyle = "#38bdf8";
    ctx.beginPath();
    ctx.arc(-24 + Math.sin(time * 3) * 4, -40, 4, 0, Math.PI * 2);
    ctx.arc(24 - Math.sin(time * 3) * 4, -40, 4, 0, Math.PI * 2);
    ctx.fill();

    // Name Badge Tag Below Bug
    ctx.restore();
    ctx.save();
    ctx.font = "bold 11px monospace";
    ctx.textAlign = "center";
    ctx.fillStyle = "#f8fafc";
    ctx.shadowColor = bug.color;
    ctx.shadowBlur = 8;
    ctx.fillText(`[ ${bug.name} ]`, bug.x, bug.y + bug.radius + 18);
    ctx.font = "9px monospace";
    ctx.fillStyle = bug.accentColor;
    ctx.fillText(bug.subtext, bug.x, bug.y + bug.radius + 30);
    ctx.restore();
  }

  // Draw Crosshair / Reticle on mouse position
  function drawCrosshair(x, y) {
    if (!mouse.isHovering) return;

    ctx.save();
    ctx.strokeStyle = "#38bdf8";
    ctx.shadowColor = "#38bdf8";
    ctx.shadowBlur = 10;
    ctx.lineWidth = 2;

    // Outer Target Ring
    ctx.beginPath();
    ctx.arc(x, y, 22, 0, Math.PI * 2);
    ctx.stroke();

    // Inner Target Ring
    ctx.beginPath();
    ctx.arc(x, y, 7, 0, Math.PI * 2);
    ctx.stroke();

    // Crosshairs lines
    ctx.beginPath();
    ctx.moveTo(x - 30, y);
    ctx.lineTo(x - 12, y);
    ctx.moveTo(x + 12, y);
    ctx.lineTo(x + 30, y);
    ctx.moveTo(x, y - 30);
    ctx.lineTo(x, y - 12);
    ctx.moveTo(x, y + 12);
    ctx.lineTo(x, y + 30);
    ctx.stroke();

    // Center Red Laser Point
    ctx.fillStyle = "#ef4444";
    ctx.shadowColor = "#ef4444";
    ctx.beginPath();
    ctx.arc(x, y, 3, 0, Math.PI * 2);
    ctx.fill();

    ctx.restore();
  }

  // Explosion Particle Generator
  function createExplosion(x, y, color) {
    for (let i = 0; i < 35; i++) {
      const angle = Math.random() * Math.PI * 2;
      const speed = Math.random() * 7 + 2;
      particles.push({
        x,
        y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: Math.random() * 4 + 2,
        color: i % 2 === 0 ? color : "#ffffff",
        alpha: 1,
        decay: Math.random() * 0.02 + 0.015,
      });
    }

    // Shockwave ring
    particles.push({
      x,
      y,
      radius: 5,
      maxRadius: 65,
      color,
      alpha: 1,
      isRing: true,
      decay: 0.04,
    });

    floatingTexts.push({
      x,
      y: y - 20,
      text: "💥 BUG ELIMINATED! +1000 PTS",
      alpha: 1,
      color: "#10b981",
    });
  }

  // Shoot Laser from bottom toward target (x, y)
  function shootLaser(targetX, targetY) {
    const rect = canvas.getBoundingClientRect();
    const startX = rect.width / 2;
    const startY = rect.height;

    lasers.push({
      x1: startX,
      y1: startY,
      x2: targetX,
      y2: targetY,
      alpha: 1,
    });

    sfx.playLaser();

    // Check hit on alien bugs
    alienBugs.forEach((bug) => {
      if (!bug.alive) return;
      const dist = Math.hypot(targetX - bug.x, targetY - bug.y);
      if (dist <= bug.radius + 15) {
        bug.alive = false;
        squashedCount++;
        sfx.playExplosion();
        createExplosion(bug.x, bug.y, bug.color);
        updateHUD();

        if (squashedCount >= 3) {
          triggerVictory();
        }
      }
    });
  }

  function updateHUD() {
    const counterText = document.getElementById("hudScoreText");
    if (counterText) {
      counterText.textContent = `BUGS SQUASHED: ${squashedCount} / 3`;
    }
  }

  // Victory sequence
  function triggerVictory() {
    gameActive = false;
    sfx.playVictory();

    floatingTexts.push({
      x: (canvas.getBoundingClientRect().width || 800) / 2,
      y: (canvas.getBoundingClientRect().height || 480) / 2,
      text: "🚀 SYSTEM STABILIZED! DEPLOYING SENIOR ENGINEER...",
      alpha: 1,
      color: "#38bdf8",
      isBig: true,
    });

    setTimeout(() => {
      revealPresentation(true);
    }, 1200);
  }

  function revealPresentation(withTransition = true) {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);

    if (withTransition) {
      gameContainer.classList.add("bug-game--completed");
      setTimeout(() => {
        gameContainer.style.display = "none";
        presentationContainer.classList.add("hero-presentation--visible");
        presentationContainer.style.display = "block";
      }, 400);
    } else {
      gameContainer.style.display = "none";
      presentationContainer.classList.add("hero-presentation--visible");
      presentationContainer.style.display = "block";
    }
  }

  function resetGame() {
    squashedCount = 0;
    gameActive = true;
    lasers = [];
    particles = [];
    floatingTexts = [];

    initBugs();
    updateHUD();

    presentationContainer.classList.remove("hero-presentation--visible");
    presentationContainer.style.display = "none";
    gameContainer.classList.remove("bug-game--completed");
    gameContainer.style.display = "block";

    resizeCanvas();
    startRenderLoop();
  }

  // Canvas Main Loop
  let time = 0;
  function render() {
    const rect = canvas.getBoundingClientRect();
    const w = rect.width;
    const h = rect.height;

    ctx.clearRect(0, 0, w, h);
    time += 0.02;

    // 1. Render Deep Space Stars
    stars.forEach((star) => {
      star.y += star.speed;
      if (star.y > h) {
        star.y = 0;
        star.x = Math.random() * w;
      }
      star.alpha += star.twinkle;
      if (star.alpha > 1 || star.alpha < 0.2) star.twinkle = -star.twinkle;

      ctx.fillStyle = `rgba(255, 255, 255, ${Math.max(0, star.alpha)})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fill();
    });

    // 2. Space Grid Perspective Lines on bottom
    ctx.save();
    ctx.strokeStyle = "rgba(2, 132, 199, 0.12)";
    ctx.lineWidth = 1;
    for (let x = 0; x <= w; x += 60) {
      ctx.beginPath();
      ctx.moveTo(x, h);
      ctx.lineTo(w / 2 + (x - w / 2) * 0.35, h * 0.65);
      ctx.stroke();
    }
    for (let y = h * 0.65; y <= h; y += 30) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(w, y);
      ctx.stroke();
    }
    ctx.restore();

    // 3. Update & Draw Alien Bugs
    alienBugs.forEach((bug) => {
      if (bug.alive) {
        // Organic sinusoidal space movement
        bug.x = bug.baseX + Math.sin(time * bug.speed + bug.phase) * (w * 0.08);
        bug.y = bug.baseY + Math.cos(time * bug.speed * 1.3 + bug.phase) * 28;
        drawBug(bug, time);
      }
    });

    // 4. Render Laser Beams
    lasers.forEach((laser, idx) => {
      ctx.save();
      ctx.strokeStyle = `rgba(56, 189, 248, ${laser.alpha})`;
      ctx.shadowColor = "#38bdf8";
      ctx.shadowBlur = 15;
      ctx.lineWidth = 4;
      ctx.beginPath();
      ctx.moveTo(laser.x1, laser.y1);
      ctx.lineTo(laser.x2, laser.y2);
      ctx.stroke();

      // Core white laser beam
      ctx.strokeStyle = `rgba(255, 255, 255, ${laser.alpha})`;
      ctx.lineWidth = 2;
      ctx.stroke();

      laser.alpha -= 0.12;
      ctx.restore();

      if (laser.alpha <= 0) lasers.splice(idx, 1);
    });

    // 5. Render Particle Explosions
    particles.forEach((p, idx) => {
      ctx.save();
      if (p.isRing) {
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 3;
        ctx.globalAlpha = p.alpha;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.stroke();
        p.radius += 3.5;
        p.alpha -= p.decay;
      } else {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.08;
        p.alpha -= p.decay;
        ctx.fillStyle = p.color;
        ctx.globalAlpha = Math.max(0, p.alpha);
        ctx.shadowColor = p.color;
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      if (p.alpha <= 0) particles.splice(idx, 1);
    });

    // 6. Floating Text feedback
    floatingTexts.forEach((t, idx) => {
      ctx.save();
      t.y -= 1.2;
      t.alpha -= 0.015;
      ctx.globalAlpha = Math.max(0, t.alpha);
      ctx.font = t.isBig ? "bold 20px 'Montserrat', sans-serif" : "bold 14px monospace";
      ctx.textAlign = "center";
      ctx.fillStyle = t.color;
      ctx.shadowColor = t.color;
      ctx.shadowBlur = 12;
      ctx.fillText(t.text, t.x, t.y);
      ctx.restore();

      if (t.alpha <= 0) floatingTexts.splice(idx, 1);
    });

    // 7. Render Reticle Crosshair
    if (gameActive) {
      drawCrosshair(mouse.x, mouse.y);
    }

    animationFrameId = requestAnimationFrame(render);
  }

  function startRenderLoop() {
    if (animationFrameId) cancelAnimationFrame(animationFrameId);
    animationFrameId = requestAnimationFrame(render);
  }

  // Pointer & Click Listeners
  canvas.addEventListener("mousemove", (e) => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = e.clientX - rect.left;
    mouse.y = e.clientY - rect.top;
    mouse.isHovering = true;
  });

  canvas.addEventListener("mouseleave", () => {
    mouse.isHovering = false;
  });

  canvas.addEventListener("click", (e) => {
    sfx.init();
    if (!gameActive) return;
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    shootLaser(x, y);
  });

  // Touch Support for mobile
  canvas.addEventListener("touchstart", (e) => {
    sfx.init();
    if (!gameActive) return;
    e.preventDefault();
    const rect = canvas.getBoundingClientRect();
    const touch = e.touches[0];
    const x = touch.clientX - rect.left;
    const y = touch.clientY - rect.top;
    mouse.x = x;
    mouse.y = y;
    shootLaser(x, y);
  });

  // Sound Toggle
  if (soundToggleBtn) {
    soundToggleBtn.addEventListener("click", () => {
      sfx.init();
      sfx.enabled = !sfx.enabled;
      soundToggleBtn.innerHTML = sfx.enabled
        ? '<i class="fa fa-volume-up" aria-hidden="true"></i> SFX: ON'
        : '<i class="fa fa-volume-off" aria-hidden="true"></i> SFX: OFF';
      soundToggleBtn.classList.toggle("hud-sound-btn--muted", !sfx.enabled);
    });
  }

  // Skip & Replay Handlers
  if (skipBtn) {
    skipBtn.addEventListener("click", () => {
      revealPresentation(false);
    });
  }

  if (replayBtn) {
    replayBtn.addEventListener("click", () => {
      resetGame();
    });
  }

  window.addEventListener("resize", () => {
    resizeCanvas();
    initStars();
  });

  // Start initial game
  resizeCanvas();
  initStars();
  initBugs();
  updateHUD();
  startRenderLoop();
}
